interface Node {
    id?: string;
    children?: any[];
}

/**
 * Either push the given node to the given tree
 * or insert it at the given position.
 *
 * If a position is given, the node will be inserted at this position.
 * If not, it will be appended at the end of the array.
 */
const pushOrInsert = (newTree: Node[], node: Node, position?: number) => {
    if (position !== undefined) {
        newTree.splice(+position, 0, node);
    } else {
        newTree.push(node);
    }
};

const recursiveMoveSubtree = (
    newTree: Node[],
    oldTree: Node[],
    nodeToMove: Node,
    newParent?: string,
    position?: number,
) => {
    oldTree.forEach((n) => {
        /**
         * We copy the node so we can manipulate it freely and avoid changing the nodes passed in as parameters
         */
        const node = {
            ...n,
        };

        /**
         * If it is the node that we are trying to move, we know that we need to remove it from the new updated tree.
         * So if it has the same id, we just avoid doing any logic.
         */
        if (node.id !== nodeToMove.id) {
            /**
             * If the node has children, we need to do the same operation for all their children.
             * So we call ourselves recursively.
             */
            if (node.children) {
                Object.assign(node, {
                    children: recursiveMoveSubtree([], node.children, nodeToMove, newParent, position),
                });
            }

            /**
             * If the current node is the new parent, we add the node to move to the node's children.
             * If there are no children, we initialize the node's children and add the node to move
             * to the list
             */
            if (node.id === newParent) {
                if (node.children) {
                    pushOrInsert(node.children, nodeToMove, position);
                } else {
                    Object.assign(node, {
                        children: [nodeToMove],
                    });
                }
            }

            newTree.push(node);
        }
    });

    return newTree;
};

/**
 * From a given tree, it moves a given node to a new given parent and updates the tree.
 * @param tree - entire tree that contains the nodes to move.
 * @param nodeToMove - node that will be moved to a new parent.
 * @param newParent - parent where the node will be moved.
 */
export const moveSubtree = (tree: Node[], nodeToMove: Node, newParent?: string, position?: number) => {
    const updatedTree = recursiveMoveSubtree([], tree, nodeToMove, newParent, position);

    if (!newParent) {
        pushOrInsert(updatedTree, nodeToMove, position);
    }
    return updatedTree;
};
